import { useContext } from "react";
import type { TemporalState } from "zundo";
import { useStore as useZustandStore } from "zustand";
import { BuilderUIContext } from "@circle-react-shared/BuilderUI/Provider/BuilderUIProvider";
import type { BuilderUIState } from "@circle-react-shared/BuilderUI/Store/types";

export const useBuilderStoreErrorMessage =
  "useBuilderStore must be used within a <BuilderUI.Provider /> component";

export const useBuilderStore = <T>(
  selector: (state: BuilderUIState) => T,
): T => {
  const store = useContext(BuilderUIContext);
  if (!store) {
    throw new Error(useBuilderStoreErrorMessage);
  }
  return useZustandStore(store, selector);
};

export const useSelectedBlock = () => {
  const value = useBuilderStore(state => state.getSelectedBlock());
  const selectedBlock = value?.block;
  const meta = value?.meta;

  const canMoveUp = useBuilderStore(state =>
    state.getCanMoveSelectedBlock("up"),
  );
  const canMoveDown = useBuilderStore(state =>
    state.getCanMoveSelectedBlock("down"),
  );
  const moveSelectedBlock = useBuilderStore(state => state.moveSelectedBlock);
  const removeSelectedBlock = useBuilderStore(
    state => state.removeSelectedBlock,
  );
  const setSelectedBlockPath = useBuilderStore(
    state => state.setSelectedBlockPath,
  );
  const duplicateSelectedBlock = useBuilderStore(
    state => state.duplicateSelectedBlock,
  );

  const resetBlockStyle = useBuilderStore(state => state.resetBlockStyle);

  return {
    selectedBlock,
    meta,
    canMoveUp,
    canMoveDown,
    moveSelectedBlock,
    removeSelectedBlock,
    setSelectedBlockPath,
    duplicateSelectedBlock,
    resetBlockStyle,
  };
};

export const useBuilderHistory = <T>(
  selector: (state: TemporalState<Pick<BuilderUIState, "pages">>) => T,
): T => {
  const store = useContext(BuilderUIContext);
  if (!store) {
    throw new Error(useBuilderStoreErrorMessage);
  }
  return useZustandStore(store.temporal, selector);
};
